import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Linking
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { Redirect, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  subscribeWithApple,
  subscribeWithStripe,
  restoreApplePurchases,
  listPrices,
  getAvailablePlans,
} from '../../../../services/subscriptionService';
import {
  getAppleIapProducts,
  isAppleIapCancellationError,
  type AppleIapProduct,
} from '../../../../services/appleIapService';
import { SettingsSubscriptionLogger } from '../../../../utils/logger';
import { isIOS, isWeb } from '../../../../utils/platform';
import theme from '../../../../config/theme';
import { useWebAlert } from '../../../../components/WebAlert';
import WebLoadingOverlay from '../../../../components/WebLoadingOverlay';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import { globalState } from '@/state/globalState';
import { APP_CONSTANTS } from '@/config/constants';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { canAccessFinancialSettings } from '@/utils/userRoles';
import { LEGAL_URLS } from '@/config/legal';

// Logger para a página de planos
const logger = SettingsSubscriptionLogger.Plans;

// Interface para os preços do Stripe
interface StripePrice {
  id: string;
  nickname: string;
  unit_amount: number;
  currency: string;
}

const PREMIUM_HIGHLIGHTS = [
  'Checklists ilimitados com fotos em alta resolução;',
  'Equipe colaborando em tempo real (até 5 usuários);',
  'Suporte via WhatsApp;',
  'Armazenamento das fotos por até 12 meses;',
  'Modelos personalizados para cada cliente ou operação.',
];

const parsePlanFeatures = (features: unknown): Record<string, unknown> => {
  if (!features) return {};
  if (typeof features === 'string') {
    try {
      return JSON.parse(features);
    } catch (_error) {
      return {};
    }
  }
  return typeof features === 'object' ? features as Record<string, unknown> : {};
};

const normalizeFeatureKey = (key: string) =>
  key.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const getFeatureValue = (features: Record<string, unknown>, keys: string[]) => {
  const normalizedKeys = keys.map(normalizeFeatureKey);
  return Object.entries(features).find(([key]) =>
    normalizedKeys.includes(normalizeFeatureKey(key))
  )?.[1];
};

const formatFeatureValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const buildPlanHighlights = (features: unknown) => {
  const parsed = parsePlanFeatures(features);
  const highlights: string[] = [];
  const users = formatFeatureValue(getFeatureValue(parsed, ['usuarios', 'usuários', 'users']));
  const photos = formatFeatureValue(getFeatureValue(parsed, ['fotos_por_checklist', 'fotos', 'num_fotos', 'limite_fotos']));
  const support = formatFeatureValue(getFeatureValue(parsed, ['suporte', 'support']));
  const storage = formatFeatureValue(getFeatureValue(parsed, ['armazenamento', 'storage']));

  if (photos) highlights.push(`Até ${photos} fotos por checklist;`);
  if (users) highlights.push(`Até ${users} usuários;`);
  if (support) highlights.push(`Suporte ${support};`);
  if (storage) highlights.push(`Armazenamento ${storage};`);

  return highlights.length > 0 ? highlights : PREMIUM_HIGHLIGHTS;
};

const PlansScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const userType = globalState.user.profile.get()?.user_type;
  const hasScreenAccess = canAccessFinancialSettings(userType);
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [localPlans, setLocalPlans] = useState<any[]>([]);
  const [appleProducts, setAppleProducts] = useState<AppleIapProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [subscribingPriceId, setSubscribingPriceId] = useState<string | null>(null); // Track which plan is being subscribed to
  const [error, setError] = useState('');
  const { showAlert } = useWebAlert();
  const empresaId = globalState.company.data.get()?.id ?? '';
  const subscriptionState = globalState.subscription.get();
  const hasActivePaidSubscription =
    (subscriptionState.status === 'ativa' || subscriptionState.status === 'cancelamento_agendado') &&
    Boolean(subscriptionState.externalId) &&
    !subscriptionState.externalId.startsWith(APP_CONSTANTS.SUBSCRIPTION_TRIAL.EXTERNAL_ID_PREFIX);

  // Buscar planos ao carregar a página
  useEffect(() => {
    if (!hasScreenAccess) {
      setIsLoading(false);
      setPrices([]);
      setLocalPlans([]);
      return;
    }

    if (isIOS) {
      if (hasActivePaidSubscription) {
        setIsLoading(false);
        setPrices([]);
        setLocalPlans([]);
        setAppleProducts([]);
        setError('');
        return;
      }
      const loadAppleProducts = async () => {
        setIsLoading(true);
        setPrices([]);
        setLocalPlans([]);
        setError('');
        try {
          setAppleProducts(await getAppleIapProducts());
        } catch (appleError: any) {
          logger.error('Erro ao carregar produtos Apple:', appleError);
          setError(appleError?.message || 'Falha ao carregar os planos da App Store.');
        } finally {
          setIsLoading(false);
        }
      };
      void loadAppleProducts();
      return;
    }

    const loadPlans = async () => {
      logger.debug('Carregando planos disponíveis');
      try {
        setIsLoading(true);
        
        // 1. Buscar planos do banco local/Supabase
        const { data: plans, error: plansError } = await getAvailablePlans();
        if (plansError) {
          logger.error('Erro ao buscar planos locais:', plansError);
          setError('Falha ao carregar planos. Tente novamente mais tarde.');
        } else {
          setLocalPlans(plans);
          logger.debug(`${plans.length} planos locais encontrados`);
        }
        
        // 2. Se estiver na web, buscar preços do Stripe via Edge Function
        if (isWeb) {
          try {
            logger.debug('Buscando preços do Stripe');
            const stripePrices = await listPrices();
            const orderedPrices = [...stripePrices].sort(
              (a, b) => (a.unit_amount ?? 0) - (b.unit_amount ?? 0)
            );
            setPrices(orderedPrices);
            logger.debug(`${stripePrices.length} preços do Stripe encontrados`);
          } catch (stripeError) {
            logger.error('Erro ao buscar preços do Stripe:', stripeError);
            setError('Falha ao carregar preços. Tente novamente mais tarde.');
          }
        }
      } catch (err) {
        logger.error('Erro ao carregar planos:', err);
        setError('Falha ao carregar planos. Tente novamente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPlans();
    
    return () => {
      logger.debug('Componente de planos desmontado');
    };
  }, [hasScreenAccess, hasActivePaidSubscription, loadAttempt]);
  
  // Assinar um plano
  const onSubscribe = async (priceId: string) => {
    // Prevent clicking again if already processing
    if (subscribingPriceId) return;
    
    logger.debug(`Solicitação de assinatura para o preço: ${priceId}`);
    try {
      // Set subscribing state
      setSubscribingPriceId(priceId);
      
      await subscribeWithStripe(priceId);
    } catch (err: any) {
      logger.error('Erro ao iniciar assinatura:', err);
      
      if (err.message === "consumption-only") {
        if (Platform.OS !== 'android') {
          await showAlert(
            "Recurso indisponível",
            "Assinaturas só podem ser feitas através do site. Por favor, acesse nosso site no navegador para assinar.",
            [{ text: 'OK' }]
          );
        } else {
          Alert.alert(
            "Recurso indisponível",
            "Assinaturas só podem ser feitas através do site. Por favor, acesse nosso site no navegador para assinar.",
          );
        }
      } else {
        if (Platform.OS !== 'android') {
          await showAlert(
            "Erro na assinatura",
            err.message || "Não foi possível iniciar o processo de assinatura. Tente novamente mais tarde.",
            [{ text: 'OK' }]
          );
        } else {
          Alert.alert(
            "Erro na assinatura",
            err.message || "Não foi possível iniciar o processo de assinatura. Tente novamente mais tarde."
          );
        }
      }
    } finally {
      // Reset subscribing state
      setSubscribingPriceId(null);
    }
  };
  
  const onSubscribeWithApple = async (productId: string) => {
    if (subscribingPriceId) return;

    try {
      setSubscribingPriceId(productId);
      await subscribeWithApple(productId, empresaId);
      router.replace('/settings/subscription');
    } catch (err: any) {
      if (isAppleIapCancellationError(err)) {
        logger.info('Assinatura Apple cancelada pelo usuario.');
        return;
      }
      logger.error('Erro ao iniciar assinatura Apple:', err);
      const safeMessages = new Set([
        'A App Store nao retornou todos os planos configurados.',
        'Catalogo Apple IAP incompleto nesta build.',
        'Empresa nao encontrada para a assinatura Apple.',
        'Nao foi possivel validar a assinatura com a Apple.',
        'Plano Apple nao autorizado.',
        'Resposta invalida ao validar assinatura Apple.',
        'Tempo esgotado ao aguardar a App Store.',
        'Transacao Apple sem comprovante assinado.',
        'Transacao Apple sem vinculo com a empresa atual.',
      ]);
      const message = safeMessages.has(err?.message)
        ? err.message
        : 'Nao foi possivel concluir a assinatura pela App Store. Tente novamente.';
      if (Platform.OS !== 'android') {
        await showAlert('Assinatura pela App Store', message, [{ text: 'OK' }]);
      } else {
        Alert.alert('Assinatura pela App Store', message);
      }
    } finally {
      setSubscribingPriceId(null);
    }
  };

  const onRestoreApple = async () => {
    if (subscribingPriceId) return;
    try {
      setSubscribingPriceId('apple-restore');
      const restored = await restoreApplePurchases(empresaId);
      const message = restored.length > 0
        ? 'Assinatura restaurada e validada com sucesso.'
        : 'Nenhuma assinatura ativa foi encontrada para esta conta Apple.';
      await showAlert('Restaurar compras', message, [{ text: 'OK' }]);
      if (restored.length > 0) router.replace('/settings/subscription');
    } catch (err: any) {
      logger.error('Erro ao restaurar assinatura Apple:', err);
      await showAlert(
        'Restaurar compras',
        err?.message || 'Nao foi possivel restaurar a assinatura.',
        [{ text: 'OK' }],
      );
    } finally {
      setSubscribingPriceId(null);
    }
  };

  const openLegalDocument = async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch (legalError) {
      logger.warn('Não foi possível abrir o documento legal:', legalError);
      await showAlert(
        'Documento indisponível',
        'Não foi possível abrir este documento agora. Tente novamente.',
        [{ text: 'OK' }],
      );
    }
  };

  const renderAppleLegalLinks = () => (
    <View style={styles.appleLegalContainer}>
      <Text style={styles.appleRenewalText}>
        Assinatura mensal auto-renovável. A cobrança é feita pela App Store e se renova todo mês até ser cancelada.
      </Text>
      <Text style={styles.appleAgreementText}>Ao assinar, você concorda com:</Text>
      <View style={styles.appleLegalLinksRow}>
        <TouchableOpacity
          style={styles.appleLegalLinkButton}
          onPress={() => openLegalDocument(LEGAL_URLS.termsOfUse)}
        >
          <Text style={styles.appleLegalLinkText}>Termos de Uso (EULA)</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.appleLegalLinkButton}
          onPress={() => openLegalDocument(LEGAL_URLS.privacyPolicy)}
        >
          <Text style={styles.appleLegalLinkText}>Política de Privacidade</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };
  
  // Renderiza o conteúdo baseado no estado
  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando planos...</Text>
        </View>
      );
    }
    
    if (error) {
      return (
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={48} color={theme.colors.util.error} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => setLoadAttempt((currentAttempt) => currentAttempt + 1)}
          >
            <Text style={styles.retryButtonText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      );
    }
    
    if (isIOS) {
      if (hasActivePaidSubscription) {
        const isApple = subscriptionState.billingProvider === 'apple';
        return (
          <View style={styles.planCard}>
            <View style={styles.planBadge}>
              <Text style={styles.planBadgeText}>{isApple ? 'App Store' : 'Plano ativo'}</Text>
            </View>
            <Text style={styles.planTitle}>Sua empresa ja possui acesso Premium</Text>
            <Text style={styles.planDescription}>
              {isApple
                ? 'A assinatura atual e gerenciada pela App Store. Nao e necessario comprar novamente.'
                : 'O acesso atual foi contratado fora da App Store. Para evitar cobranca duplicada, uma nova assinatura Apple nao sera iniciada.'}
            </Text>
            <TouchableOpacity
              style={styles.restoreButton}
              onPress={onRestoreApple}
              disabled={subscribingPriceId !== null}
            >
              <Text style={styles.restoreButtonText}>Restaurar compras da App Store</Text>
            </TouchableOpacity>
          </View>
        );
      }

      return (
        <View>
          {appleProducts.map((product) => (
            <View key={product.id} style={styles.planCard}>
              <View style={styles.planBadge}>
                <Text style={styles.planBadgeText}>App Store - {product.tier} fotos</Text>
              </View>
              <Text style={styles.planTitle}>{product.title}</Text>
              <Text style={styles.planDescription}>{product.description}</Text>
              <View style={styles.priceRow}>
                <Text style={styles.priceValue}>{product.displayPrice}</Text>
                <Text style={styles.priceSuffix}>/ mes</Text>
              </View>
              <View style={styles.highlightList}>
                <View style={styles.highlightItem}>
                  <MaterialCommunityIcons name="check-circle" size={20} color={theme.colors.util.success} />
                  <Text style={styles.highlightText}>Ate {product.tier} fotos por checklist;</Text>
                </View>
                {PREMIUM_HIGHLIGHTS.slice(1).map((item) => (
                  <View key={item} style={styles.highlightItem}>
                    <MaterialCommunityIcons name="check-circle" size={20} color={theme.colors.util.success} />
                    <Text style={styles.highlightText}>{item}</Text>
                  </View>
                ))}
              </View>
              {renderAppleLegalLinks()}
              <TouchableOpacity
                style={[
                  styles.subscribeButton,
                  subscribingPriceId === product.id && styles.disabledButton,
                ]}
                onPress={() => onSubscribeWithApple(product.id)}
                disabled={subscribingPriceId !== null}
              >
                {subscribingPriceId === product.id ? (
                  <View style={styles.buttonLoadingContainer}>
                    <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                    <Text style={styles.subscribeButtonText}> Processando...</Text>
                  </View>
                ) : (
                  <Text style={styles.subscribeButtonText}>Assinar pela App Store</Text>
                )}
              </TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity
            style={styles.restoreButton}
            onPress={onRestoreApple}
            disabled={subscribingPriceId !== null}
          >
            {subscribingPriceId === 'apple-restore' ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              <Text style={styles.restoreButtonText}>Restaurar compras</Text>
            )}
          </TouchableOpacity>
        </View>
      );
    }

    // Na versão web, mostrar os preços do Stripe
    if (isWeb && prices.length > 0) {
      const highestPriceAmount = Math.max(...prices.map((price) => price.unit_amount ?? 0));
      return (
        <View>
          {prices.map((price) => {
            const linkedPlan = localPlans.find(
              (plan) => plan.external_price_id === price.id
            );
            const formattedPrice = new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: price.currency.toUpperCase(),
            }).format(price.unit_amount / 100);
            const planHighlights = buildPlanHighlights(linkedPlan?.features);
            const planBadgeText = prices.length > 1
              ? (price.unit_amount === highestPriceAmount ? 'Mais completo' : 'Essencial')
              : 'Plano recomendado';

            return (
              <View key={price.id} style={styles.planCard}>
                <View style={styles.planBadge}>
                  <Text style={styles.planBadgeText}>{planBadgeText}</Text>
                </View>

                <Text style={styles.planTitle}>
                  {linkedPlan?.name || price.nickname || 'Premium Guincho'}
                </Text>
                <Text style={styles.planDescription}>
                  {linkedPlan?.description ||
                    'Simplifique o dia a dia da sua equipe, automatize registros e ofereça relatórios impecáveis aos seus clientes.'}
                </Text>

                <View style={styles.priceRow}>
                  <Text style={styles.priceValue}>{formattedPrice}</Text>
                  <Text style={styles.priceSuffix}>/ mês</Text>
                </View>
                <Text style={styles.priceSubtext}>Sem taxas escondidas • Cancele quando quiser</Text>

                <View style={styles.highlightList}>
                  {planHighlights.map((item) => (
                    <View key={item} style={styles.highlightItem}>
                      <MaterialCommunityIcons name="check-circle" size={20} color={theme.colors.util.success} />
                      <Text style={styles.highlightText}>{item}</Text>
                    </View>
                  ))}
                </View>

                <TouchableOpacity
                  style={[
                    styles.subscribeButton,
                    subscribingPriceId === price.id && styles.disabledButton
                  ]}
                  onPress={() => onSubscribe(price.id)}
                  disabled={subscribingPriceId !== null}
                >
                  {subscribingPriceId === price.id ? (
                    <View style={styles.buttonLoadingContainer}>
                      <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                      <Text style={styles.subscribeButtonText}> Processando...</Text>
                    </View>
                  ) : (
                    <Text style={styles.subscribeButtonText}>Assinar agora</Text>
                  )}
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      );
    }
    
    // No mobile ou se não houver preços do Stripe, mostrar os planos locais
    // ou uma mensagem de recurso indisponível
    if (!isWeb) {
      return (
        <View style={styles.mobilePromptWrapper}>
          <UpgradePrompt variant="home" />
        </View>
      );
    }
    
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={theme.colors.text.secondary} />
        <Text style={styles.emptyText}>Nenhum plano disponível no momento.</Text>
      </View>
    );
  };

  if (!hasScreenAccess) {
    return <Redirect href="/(app)/settings" />;
  }
  
  return (
    <View style={styles.container}>
      <WebLoadingOverlay
        visible={isLoading && isWeb}
        message="Carregando planos. Por favor, aguarde enquanto preparamos os planos disponíveis..."
      />
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{isIOS ? 'Acesso' : 'Planos'}</Text>
        <View style={styles.headerRight} />
      </View>
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {renderContent()}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: theme.spacing.md,
    paddingBottom: theme.spacing.xl,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
    minHeight: 300,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
    minHeight: 300,
  },
  errorText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
  },
  retryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  planCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    borderWidth: 1,
    borderColor: `${theme.colors.primary.default}22`,
    ...theme.shadows.md,
  },
  planBadge: {
    alignSelf: 'flex-start',
    backgroundColor: `${theme.colors.primary.default}12`,
    borderRadius: theme.borderRadius.pill,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  },
  planBadgeText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.semibold as any,
  },
  planTitle: {
    fontSize: theme.fontSizes.xl,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.bold as any,
    marginBottom: theme.spacing.xs,
  },
  planDescription: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    lineHeight: theme.fontSizes.lg,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: theme.spacing.lg,
  },
  priceValue: {
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.primary.default,
  },
  priceSuffix: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginLeft: theme.spacing.xs,
  },
  priceSubtext: {
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.tertiary,
  },
  highlightList: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    marginBottom: theme.spacing.lg,
  },
  highlightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: theme.spacing.sm,
  },
  highlightText: {
    flex: 1,
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    lineHeight: theme.fontSizes.md,
  },
  subscribeButton: {
    backgroundColor: theme.colors.util.success,
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    alignSelf: Platform.select({ web: 'center', default: 'stretch' }),
    minWidth: Platform.select({ web: 220, default: undefined }),
  },
  disabledButton: {
    backgroundColor: `${theme.colors.util.success}80`,
  },
  restoreButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  restoreButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
  },
  appleLegalContainer: {
    marginTop: -theme.spacing.sm,
    marginBottom: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
  },
  appleRenewalText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  appleAgreementText: {
    marginTop: theme.spacing.sm,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
  },
  appleLegalLinksRow: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing.xs,
  },
  appleLegalLinkButton: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xs,
  },
  appleLegalLinkText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
    textDecorationLine: 'underline',
  },
  buttonLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subscribeButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
    minHeight: 300,
  },
  emptyText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  mobilePromptWrapper: {
    flex: 1,
    paddingVertical: theme.spacing.lg,
  },
});

export default PlansScreen; 
